import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import Select from 'react-select'
import { fetchVodAssets, fetchVodTypes } from '../services/vodApi'
import { api } from '../services/api'
import { Badge, Button, Pagination, SortableHeader } from '../components'
import type { SortDirection } from '../components'
import { formatPacificDateTimeParts } from '../utils/timezone'
import { useVodsFilters } from '../hooks'
import { CompetitorSelect, SeasonSelect } from '../components'
import type { Competitor, Season } from '../types/forms'
import {
  VodAsset,
  VodOrigin,
  VodOriginFilter,
  VodPackagingState,
  VodPublishState,
  VodMatchLinkFilter,
  VodPlayableFilter,
  VodSortColumn,
} from '../types/vod'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './VodsList.module.scss'
import { originVariant } from '../utils/vodOrigin'

// The machine-ingestion review queue. Mirrors the API's UNASSIGNED_TYPE_CODE.
const UNASSIGNED_TYPE_CODE = 'UNASSIGNED'

const ORIGIN_OPTIONS = [
  { value: 'NON_VIRTUAL', label: 'All Real Sources' },
  { value: '', label: 'All Sources (incl. Virtual)' },
  { value: 'AMAGI', label: 'Amagi' },
  { value: 'WSC', label: 'WSC' },
  { value: 'PDM', label: 'PDM' },
  { value: 'TENNIS_CHANNEL', label: 'Tennis Channel' },
  { value: 'VIRTUAL', label: 'Virtual' },
  { value: 'UNKNOWN', label: 'Unknown' },
] as const

// VIRTUAL is omitted: the Source dropdown already isolates virtual placeholders.
const PUBLISH_STATE_OPTIONS: { value: VodPublishState; label: string }[] = [
  { value: 'ACTIVE', label: 'Published' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'AWAITING_PACKAGING', label: 'Awaiting packaging' },
  { value: 'AWAITING_MATCH', label: 'Awaiting match' },
  { value: 'HIDDEN', label: 'Unpublished' },
  { value: 'EXPIRED', label: 'Expired' },
]

const PACKAGING_LABEL: Record<VodPackagingState, string> = {
  PENDING: 'Queued',
  SUBMITTED: 'Packaging\u2026',
  PROGRESSING: 'Packaging\u2026',
  ERROR: 'Pkg failed',
  CANCELED: 'Canceled',
}

const PACKAGING_VARIANT: Record<
  VodPackagingState,
  'success' | 'warning' | 'error' | 'neutral' | 'secondary'
> = {
  PENDING: 'warning',
  SUBMITTED: 'secondary',
  PROGRESSING: 'secondary',
  ERROR: 'error',
  CANCELED: 'neutral',
}

const PUBLISH_STATE_LABEL: Record<VodPublishState, string> = {
  ACTIVE: 'Published',
  SCHEDULED: 'Scheduled',
  AWAITING_PACKAGING: 'Awaiting packaging',
  AWAITING_MATCH: 'Awaiting match',
  VIRTUAL: 'Virtual placeholder',
  HIDDEN: 'Unpublished',
  EXPIRED: 'Expired',
}

const PUBLISH_STATE_VARIANT: Record<
  VodPublishState,
  'success' | 'warning' | 'error' | 'neutral'
> = {
  ACTIVE: 'success',
  SCHEDULED: 'warning',
  AWAITING_PACKAGING: 'warning',
  AWAITING_MATCH: 'warning',
  VIRTUAL: 'neutral',
  HIDDEN: 'neutral',
  EXPIRED: 'error',
}

const MATCH_LINK_OPTIONS: Array<{ value: VodMatchLinkFilter; label: string }> = [
  { value: '', label: 'Any Match Link' },
  { value: 'MISSING', label: 'Unlinked' },
  { value: 'LINKED', label: 'Linked' },
]

const PLAYABLE_OPTIONS: Array<{ value: VodPlayableFilter; label: string }> = [
  { value: '', label: 'Any Playability' },
  { value: 'YES', label: 'Playable' },
  { value: 'NO', label: 'Not Playable' },
]

/** The header row, in column order. Text sorts A-Z first; everything else
 *  reads newest or "most" first, which is what an editor scans for. */
const SORTABLE_HEADERS: Array<{
  column: VodSortColumn
  label: string
  defaultDirection: SortDirection
}> = [
  { column: 'title', label: 'Title', defaultDirection: 'asc' },
  { column: 'vod_type_code', label: 'Type', defaultDirection: 'asc' },
  { column: 'origin', label: 'Source', defaultDirection: 'asc' },
  { column: 'playable', label: 'Playable', defaultDirection: 'desc' },
  { column: 'match_link', label: 'Match', defaultDirection: 'desc' },
  { column: 'publish_state', label: 'Publishing', defaultDirection: 'asc' },
  { column: 'display_date', label: 'Display Date', defaultDirection: 'desc' },
]

const VodsList: React.FC = () => {
  const navigate = useNavigate()
  const { filters, updateFilterAndResetPage, handlePageChange } = useVodsFilters()
  const {
    vodTypeCode,
    origin,
    publishState,
    dateFrom,
    dateTo,
    search,
    matchId,
    tags,
    competitorIds,
    seasonIds,
    matchLink,
    playable,
    sortBy,
    sortOrder,
    page,
  } = filters
  const limit = 50

  // VOD types (for the type filter) come from the DB, not a hardcoded list
  const { data: typesData } = useSWR('vod-types', () => fetchVodTypes(), {
    revalidateOnFocus: false,
  })
  const typeOptions = React.useMemo(
    () => [
      ...(typesData?.types || [])
        // Deduped: migration 110 only converges is_active=FALSE when re-applied,
        // so an environment that once activated the sentinel by hand would
        // otherwise render two options sharing one value.
        .filter((t) => t.vod_type_code !== UNASSIGNED_TYPE_CODE)
        .map((t) => ({ value: t.vod_type_code, label: t.display_name })),
      // Appended rather than served by GET /vod/types: that endpoint is
      // active-only, which is what keeps UNASSIGNED out of the create form and
      // the detail page's type picker. It is a thing to filter BY, never a type
      // an editor may assign. The API excludes it unless named here.
      { value: UNASSIGNED_TYPE_CODE, label: 'Unassigned (needs review)' },
    ],
    [typesData]
  )
  const selectedTypeOptions = React.useMemo(
    () =>
      // Same shape as selectedTagOptions below: the filter now accepts any type
      // that exists in data, including deactivated ones, so a code with no
      // option left still needs a chip or the list is filtered by something
      // invisible and unclearable.
      vodTypeCode.map(
        (code) =>
          typeOptions.find((opt) => opt.value === code) ?? {
            value: code,
            label: code,
          }
      ),
    [typeOptions, vodTypeCode]
  )
  const selectedPublishStateOptions = React.useMemo(
    () => PUBLISH_STATE_OPTIONS.filter((opt) => publishState.includes(opt.value)),
    [publishState]
  )

  // The merchandising vocabulary the VOD pages create and edit. A key with no
  // option left keeps its own chip so a stale URL stays editable.
  // The URL carries ids; the pickers show names. Resolving them keeps a
  // shared link legible instead of rendering two empty boxes that filter.
  const { data: competitorData } = useSWR(
    competitorIds.length > 0 ? ['vods-filter-competitors', competitorIds] : null,
    () => api.get('/competitors', { ids: competitorIds.join(','), limit: 100 }),
    { revalidateOnFocus: false }
  )
  const selectedCompetitors: Competitor[] = React.useMemo(
    () =>
      ((competitorData?.competitors ?? []) as Array<Record<string, string>>).map(
        (c) => ({
          tc_id: c.tc_competitor_id,
          ext_id: c.ext_competitor_id ?? '',
          name: c.tc_name || c.ext_name || 'Unknown',
          team_type: c.tc_team_type || c.ext_team_type || 'singles',
        })
      ),
    [competitorData]
  )

  const { data: seasonData } = useSWR(
    seasonIds.length > 0 ? ['vods-filter-seasons', seasonIds] : null,
    () => api.get('/seasons', { ids: seasonIds.join(','), limit: 100 }),
    { revalidateOnFocus: false }
  )
  const selectedSeasons: Season[] = React.useMemo(
    () =>
      ((seasonData?.seasons ?? []) as Array<Record<string, string>>).map((s) => ({
        tc_id: s.tc_season_id,
        ext_id: s.ext_season_id ?? '',
        name: s.tc_name || s.ext_name || 'Unknown season',
        year: Number(s.tc_year || s.ext_year || 0),
      })),
    [seasonData]
  )

  const handleSort = React.useCallback(
    (column: VodSortColumn, direction: SortDirection) => {
      updateFilterAndResetPage({ sortBy: column, sortOrder: direction })
    },
    [updateFilterAndResetPage]
  )

  const { data: tagsData } = useSWR<{
    tags: Array<{ value: string; label: string }>
  }>('publishing-tag-options', () => api.get('/publishing/tag-options'), {
    revalidateOnFocus: false,
  })
  const tagOptions = React.useMemo(
    () =>
      (tagsData?.tags || []).map((t) => ({ value: t.value, label: t.label })),
    [tagsData]
  )
  const selectedTagOptions = React.useMemo(
    () =>
      tags.map(
        (key) =>
          tagOptions.find((opt) => opt.value === key) || {
            value: key,
            label: key,
          }
      ),
    [tags, tagOptions]
  )

  const { data, isLoading, error, mutate } = useSWR(
    [
      'vods',
      {
        vodTypeCode,
        origin,
        publishState,
        dateFrom,
        dateTo,
        search,
        matchId,
        tags,
        competitorIds,
        seasonIds,
        matchLink,
        playable,
        sortBy,
        sortOrder,
        page,
        limit,
      },
    ],
    () =>
      fetchVodAssets({
        vod_type_code: vodTypeCode.length > 0 ? vodTypeCode : undefined,
        origin: origin || undefined,
        publish_state: publishState.length > 0 ? publishState : undefined,
        display_date_from: dateFrom || undefined,
        display_date_to: dateTo || undefined,
        search: search || undefined,
        match_id: matchId || undefined,
        tags: tags.length > 0 ? tags : undefined,
        competitor_ids: competitorIds.length > 0 ? competitorIds : undefined,
        season_ids: seasonIds.length > 0 ? seasonIds : undefined,
        match_link: matchLink || undefined,
        playable: playable || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        limit,
      }),
    { revalidateOnFocus: false }
  )

  const assets: VodAsset[] = data?.assets || []
  const pagination = data?.pagination

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const originLabel = (o?: VodOrigin) =>
    ORIGIN_OPTIONS.find((opt) => opt.value === o)?.label || o || '—'

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading VODs</h2>
          <p>Failed to fetch VODs: {(error as Error).message}</p>
          <Button variant="secondary" onClick={() => mutate()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>VODs</h1>
        <div className={pageStyles.headerButtons}>
          <Button onClick={() => navigate('/vods/new')}>
            + New VOD
          </Button>
        </div>
      </div>

      {matchId && (
        <div className={styles.matchFilterBanner}>
          <span>
            Showing VODs for match:{' '}
            <code className={styles.matchIdCode}>{matchId}</code>
          </span>
          <Button
            variant="secondary"
            size="small"
            onClick={() => updateFilterAndResetPage({ matchId: '' })}
          >
            Clear Filter
          </Button>
          <Link to={`/matches/${matchId}`}>
            <Button variant="secondary" size="small">
              View Match
            </Button>
          </Link>
        </div>
      )}

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={styles.filters}>
            <div className={styles.field}>
              <label htmlFor="search">Search:</label>
              <input
                type="text"
                id="search"
                value={search}
                onChange={(e) =>
                  updateFilterAndResetPage({ search: e.target.value })
                }
                placeholder="Title, description, asset ID, or match ID / SR ID"
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={`${styles.field} ${styles.multiField}`}>
              <label htmlFor="vodTypeCode">Type:</label>
              <Select
                inputId="vodTypeCode"
                isMulti
                isClearable
                // Not "All Types": the empty filter excludes UNASSIGNED, so the
                // placeholder has to say what it actually shows.
                placeholder="All Assigned Types"
                value={selectedTypeOptions}
                onChange={(options) =>
                  updateFilterAndResetPage({
                    vodTypeCode: options ? options.map((opt) => opt.value) : [],
                  })
                }
                options={typeOptions}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="origin">Source:</label>
              <Select
                inputId="origin"
                value={
                  ORIGIN_OPTIONS.find((opt) => opt.value === origin) ||
                  ORIGIN_OPTIONS[0]
                }
                onChange={(option) =>
                  updateFilterAndResetPage({
                    origin: (option?.value ?? '') as VodOriginFilter,
                  })
                }
                options={ORIGIN_OPTIONS}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
            <div className={`${styles.field} ${styles.tagsField}`}>
              <label htmlFor="tags">Merchandising Tags:</label>
              <Select
                inputId="tags"
                isMulti
                isClearable
                placeholder="All Tags"
                value={selectedTagOptions}
                onChange={(options) =>
                  updateFilterAndResetPage({
                    tags: options ? options.map((opt) => opt.value) : [],
                  })
                }
                options={tagOptions}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />
            </div>
            <div className={`${styles.field} ${styles.tagsField}`}>
              <label htmlFor="competitors">Players &amp; Teams:</label>
              <CompetitorSelect
                isMulti
                teamType="mixed"
                value={selectedCompetitors.length > 0 ? selectedCompetitors : null}
                onChange={(value) =>
                  updateFilterAndResetPage({
                    competitorIds: (Array.isArray(value)
                      ? value
                      : value
                        ? [value]
                        : []
                    ).map((c) => c.tc_id),
                  })
                }
                placeholder="All Players"
              />
            </div>
            <div className={`${styles.field} ${styles.tagsField}`}>
              <label htmlFor="seasons">Seasons:</label>
              <SeasonSelect
                isMulti
                value={selectedSeasons.length > 0 ? selectedSeasons : null}
                onChange={(value) =>
                  updateFilterAndResetPage({
                    seasonIds: (Array.isArray(value)
                      ? value
                      : value
                        ? [value]
                        : []
                    ).map((s) => s.tc_id),
                  })
                }
                placeholder="All Seasons"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="matchLink">Match Link:</label>
              <Select
                id="matchLink"
                value={
                  MATCH_LINK_OPTIONS.find((opt) => opt.value === matchLink) ||
                  MATCH_LINK_OPTIONS[0]
                }
                onChange={(option) =>
                  updateFilterAndResetPage({
                    matchLink: (option?.value || '') as VodMatchLinkFilter,
                  })
                }
                options={MATCH_LINK_OPTIONS}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="playable">Playable:</label>
              <Select
                id="playable"
                value={
                  PLAYABLE_OPTIONS.find((opt) => opt.value === playable) ||
                  PLAYABLE_OPTIONS[0]
                }
                onChange={(option) =>
                  updateFilterAndResetPage({
                    playable: (option?.value || '') as VodPlayableFilter,
                  })
                }
                options={PLAYABLE_OPTIONS}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="dateFrom">Date From:</label>
              <input
                type="date"
                id="dateFrom"
                value={dateFrom}
                onChange={(e) =>
                  updateFilterAndResetPage({ dateFrom: e.target.value })
                }
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="dateTo">Date To:</label>
              <input
                type="date"
                id="dateTo"
                value={dateTo}
                onChange={(e) =>
                  updateFilterAndResetPage({ dateTo: e.target.value })
                }
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={`${styles.field} ${styles.multiField}`}>
              <label htmlFor="publishState">Publishing:</label>
              <Select
                inputId="publishState"
                isMulti
                isClearable
                placeholder="All Publishing States"
                value={selectedPublishStateOptions}
                onChange={(options) =>
                  updateFilterAndResetPage({
                    publishState: options
                      ? options.map((opt) => opt.value)
                      : [],
                  })
                }
                options={PUBLISH_STATE_OPTIONS}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading VODs...</h3>
          <p>Please wait while we fetch the catalog.</p>
        </div>
      ) : assets.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No VODs found</h3>
          <p>No VODs are available for the selected filters.</p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            {SORTABLE_HEADERS.map(({ column, label, defaultDirection }) => (
              <SortableHeader
                key={column}
                column={column}
                activeColumn={sortBy}
                direction={sortOrder}
                defaultDirection={defaultDirection}
                onSort={handleSort}
              >
                {label}
              </SortableHeader>
            ))}
            <div></div>
          </div>

          <div className={listTableStyles.tableBody}>
            {assets.map((asset) => (
              <div
                key={asset.tc_vod_asset_id}
                className={`${listTableStyles.tableRow} ${styles.listItem}`}
              >
                <div className={listTableStyles.cellPrimary}>
                  <Link
                    to={`/vods/${asset.tc_vod_asset_id}`}
                    className={styles.titleLink}
                  >
                    <p className={listTableStyles.primaryText}>
                      {asset.tc_title}
                    </p>
                  </Link>
                  <p className={listTableStyles.secondaryText}>
                    {formatDuration(asset.duration_sec)}
                  </p>
                </div>

                <div className={listTableStyles.cellText}>
                  {asset.vod_type_display_name || asset.vod_type_code || 'N/A'}
                </div>

                <div className={listTableStyles.cellText}>
                  <Badge variant={originVariant(asset.origin)}>
                    {originLabel(asset.origin)}
                  </Badge>
                </div>

                <div className={listTableStyles.cellText}>
                  {asset.packaging ? (
                    <Badge variant={PACKAGING_VARIANT[asset.packaging.state]}>
                      {PACKAGING_LABEL[asset.packaging.state]}
                    </Badge>
                  ) : asset.has_playable_rendition ? (
                    <Badge variant="success">Playable</Badge>
                  ) : (
                    <Badge variant="neutral">None</Badge>
                  )}
                </div>

                <div className={listTableStyles.cellText}>
                  {asset.match?.tc_match_id ? (
                    <Link
                      to={`/matches/${asset.match.tc_match_id}`}
                      className={styles.matchLink}
                    >
                      <Badge variant="success" icon="link">
                        Linked
                      </Badge>
                    </Link>
                  ) : (
                    <Badge variant="neutral">Unlinked</Badge>
                  )}
                </div>

                <div
                  className={listTableStyles.cellText}
                  title={asset.publish_state}
                >
                  <Badge
                    variant={
                      PUBLISH_STATE_VARIANT[asset.publish_state] || 'neutral'
                    }
                  >
                    {PUBLISH_STATE_LABEL[asset.publish_state] ||
                      asset.publish_state ||
                      '—'}
                  </Badge>
                  {asset.has_amagi_delivery_error && (
                    <span title="Amagi rejected this VOD's current media, or the send never reached them. Open the VOD's Audit Log for the error.">
                      <Badge variant="error">Delivery</Badge>
                    </span>
                  )}
                </div>

                <div className={listTableStyles.cellText}>
                  {formatPacificDateTimeParts(asset.display_date)?.date ?? 'N/A'}
                </div>

                <div className={styles.actionsCell}>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => navigate(`/vods/${asset.tc_vod_asset_id}`)}
                  >
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {pagination && pagination.total_pages > 1 && (
            <Pagination pagination={pagination} onPageChange={handlePageChange} />
          )}
        </>
      )}
    </div>
  )
}

export default VodsList
